import ClassNames from 'classnames';
import { forEach, get, isFinite, some } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { LAZY_LOAD_CLASSNAME } from 'constants/lazyLoading';
import CloudinaryRemoteFetchImage from 'models/CloudinaryRemoteFetchImage';

const { getSrc } = CloudinaryRemoteFetchImage;

export { getSrc };

const SUPPORTED_DPRS = {
  1: 1,
  2: 2,
};

// Display warning if breakpoints are not "<Number>": <Number> pairs
export const validateBreakpoints = (props, propName) => {
  const breakpoints = get(props, propName);
  const hasError = some(
    breakpoints,
    (value, key) => !isFinite(parseInt(key, 10)) || !isFinite(value)
  );

  if (hasError) {
    return new Error('Invalid breakpoints');
  }
  return undefined;
};

const Image = class extends Component {
  static propTypes = {
    alt: PropTypes.string,
    src: PropTypes.string,
    transformations: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
      bgColor: PropTypes.string,
      format: PropTypes.string,
      quality: PropTypes.string,
    }),
    isLazy: PropTypes.bool,
    breakpoints: validateBreakpoints,
    className: PropTypes.string,
    styles: PropTypes.shape({}),
  };

  static defaultProps = {
    alt: '',
    src: null,
    transformations: {},
    breakpoints: null,
    className: null,
    styles: null,
    isLazy: true,
  };

  static getSrc = (src, transformations) => {
    const image = new CloudinaryRemoteFetchImage(src, transformations);
    return image.src;
  };

  get src() {
    const { src, transformations } = this.props;

    return Image.getSrc(src, transformations);
  }

  get sizes() {
    const { breakpoints } = this.props;
    if (!breakpoints) {
      return null;
    }

    let sizes = '';

    forEach(this.props.breakpoints, (width, breakpoint) => {
      sizes += `(max-width: ${breakpoint}px) ${width}px,`;
    });

    return sizes;
  }

  get srcSet() {
    const { src, transformations, breakpoints } = this.props;
    if (!breakpoints) {
      return null;
    }

    let srcSet = '';

    forEach(this.props.breakpoints, width => {
      forEach(SUPPORTED_DPRS, DPR => {
        const realWidth = width * DPR;
        const img = Image.getSrc(src, { ...transformations, width: realWidth });
        srcSet += `${img} ${realWidth}w,`;
      });
    });

    return srcSet;
  }

  render() {
    const { className, alt, isLazy, ...otherProps } = this.props;
    const { srcSet, sizes } = this;

    const classNames = ClassNames('Image', { [LAZY_LOAD_CLASSNAME]: isLazy }, className);

    const lazyProps = isLazy
      ? {
          'data-src': this.src,
          'data-srcset': srcSet,
        }
      : {
          src: this.src,
          srcSet,
        };

    return (
      <>
        <img className={classNames} alt={alt} sizes={sizes} {...lazyProps} {...otherProps} />

        <style jsx>
          {`
            .Image {
              color: transparent; /* Firefox alt text fix */
              max-width: 100%;
            }
          `}
        </style>
      </>
    );
  }
};

export default Image;
